import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMobileAuth } from './MobileAuthContext';

const MobileChatContext = createContext(null);

export const MobileChatProvider = ({ children }) => {
  const { api, operator, token } = useMobileAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch chat rooms for this operator
  const fetchRooms = useCallback(async () => {
    if (!api || !operator) return;
    
    try {
      const response = await api.get('/api/chat/rooms');
      setRooms(response.data);
      
      // Calculate unread count
      const unread = response.data.reduce((acc, room) => {
        return acc + (room.unread_count || 0);
      }, 0);
      setUnreadCount(unread);
    } catch (err) {
      console.error('Failed to fetch chat rooms:', err);
    }
  }, [api, operator]);

  // Fetch messages for a room
  const fetchMessages = useCallback(async (roomId) => {
    if (!api || !roomId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/api/chat/rooms/${roomId}/messages`);
      setMessages(response.data);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Connect to WebSocket for a room
  const connectToRoom = useCallback((roomId) => {
    if (!token || !roomId) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Build WebSocket URL - handle both local and production environments
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let wsUrl;
    
    if (backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1')) {
      // Local development - connect directly to backend
      wsUrl = `ws://localhost:8001/ws/chat/${roomId}?token=${token}`;
    } else {
      // Production - use the same host as the backend URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${roomId}?token=${token}`;
    }
    
    console.log('Mobile WebSocket connecting to:', wsUrl);
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('Mobile WebSocket connected to room:', roomId);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'message') {
          setMessages(prev => [...prev, data]);
          
          // Update room's last message
          setRooms(prev => prev.map(room => 
            room.id === roomId 
              ? { ...room, last_message: data.content, last_message_at: data.created_at }
              : room
          ));
        } else if (data.type === 'user_joined' || data.type === 'user_left') {
          setMessages(prev => [...prev, {
            id: `system-${Date.now()}`,
            type: 'system',
            content: `${data.user_name} ${data.type === 'user_joined' ? 'joined' : 'left'} the chat`,
            created_at: data.timestamp
          }]);
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };
    
    ws.onclose = (event) => {
      console.log('Mobile WebSocket closed:', event.code, event.reason);
      if (event.code !== 1000 && activeRoom?.id === roomId) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connectToRoom(roomId);
        }, 3000);
      }
    };
    
    ws.onerror = (error) => {
      console.error('Mobile WebSocket error:', error);
    };
    
    wsRef.current = ws;
  }, [token, activeRoom]);

  // Send a message
  const sendMessage = useCallback(async (content, messageType = 'text', jobId = null) => {
    if (!activeRoom) return;
    
    const messageData = {
      type: 'message',
      content,
      message_type: messageType,
      job_id: jobId
    };
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(messageData));
    } else {
      try {
        const response = await api.post(`/api/chat/rooms/${activeRoom.id}/messages`, {
          content,
          message_type: messageType,
          job_id: jobId
        });
        // Add message to local state immediately
        setMessages(prev => [...prev, response.data]);
      } catch (err) {
        console.error('Failed to send message:', err);
        throw err;
      }
    }
  }, [activeRoom, api]);

  // Polling for new messages (fallback when WebSocket is unreliable)
  const pollIntervalRef = useRef(null);
  
  const startPolling = useCallback((roomId) => {
    // Poll every 3 seconds for new messages
    pollIntervalRef.current = setInterval(async () => {
      if (!api || !roomId) return;
      try {
        const response = await api.get(`/api/chat/rooms/${roomId}/messages`);
        setMessages(response.data);
      } catch (err) {
        console.error('Mobile polling failed:', err);
      }
    }, 3000);
  }, [api]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Open a specific room
  const openRoom = useCallback(async (room) => {
    // If room is null, just go back to room list
    if (!room) {
      stopPolling();
      setActiveRoom(null);
      setMessages([]);
      fetchRooms();
      return;
    }
    
    setActiveRoom(room);
    setIsOpen(true);
    await fetchMessages(room.id);
    connectToRoom(room.id);
    // Start polling as fallback
    startPolling(room.id);
  }, [fetchMessages, connectToRoom, startPolling, stopPolling, fetchRooms]);

  // Open job chat
  const openJobChat = useCallback(async (jobId) => {
    if (!api) return;
    
    try {
      const response = await api.get(`/api/chat/job/${jobId}/room`);
      openRoom(response.data);
    } catch (err) {
      console.error('Failed to get job chat room:', err);
    }
  }, [api, openRoom]);

  // Close chat
  const closeChat = useCallback(() => {
    setIsOpen(false);
    setActiveRoom(null);
    setMessages([]);
    
    // Stop polling
    stopPolling();
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User closed chat');
      wsRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  }, [stopPolling]);

  // Fetch rooms on mount
  useEffect(() => {
    if (operator) {
      fetchRooms();
    }
  }, [operator, fetchRooms]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [stopPolling]);

  const value = {
    rooms,
    activeRoom,
    messages,
    isOpen,
    loading,
    unreadCount,
    fetchRooms,
    openRoom,
    closeChat,
    sendMessage,
    openJobChat,
    setIsOpen
  };

  return (
    <MobileChatContext.Provider value={value}>
      {children}
    </MobileChatContext.Provider>
  );
};

export const useMobileChat = () => {
  const context = useContext(MobileChatContext);
  if (!context) {
    throw new Error('useMobileChat must be used within a MobileChatProvider');
  }
  return context;
};

export default MobileChatContext;
